import { useRef, useState, type FormEvent, type RefObject } from 'react';
import { BORNES_BULLE } from '@nextilt/commun';
import { teinteOuAccent } from '../apparence';
import './BarreAjout.css';

/**
 * V0 · Ajouter, en rafale — design/liste.html et design/carte.html (« Ajouter dans … »)
 *
 * **La barre ne se ferme jamais.** On écrit, Entrée, on écrit. Pas de mode, pas de touche à
 * retenir, pas six fenêtres à ouvrir pour vider sa tête. C'est le geste le plus fréquent de
 * l'app, et le seul dont l'échec fait perdre l'idée.
 *
 * Un titre et rien d'autre : la date et la durée se choisiront plus tard, ou
 * jamais. Les notes s'écrivent dans la fiche. Et chaque ligne posée là est **déjà une
 * condition** — c'est ce qui rend la construction de l'arbre gratuite (SPEC §3).
 */
export function BarreAjout({
  dans,
  couleur,
  vive = false,
  champ: champDeLaFeuille,
  onAjouter,
}: {
  /** Le titre de la feuille où l'on écrit. La destination est toujours déjà remplie. */
  dans: string;
  /** Le thème du dossier : la barre porte sa couleur, pour qu'on sache où l'on écrit. */
  couleur: string | null;
  /**
   * Sur une feuille VIDE, la barre est l'invitation : elle se lève, se teinte et déborde.
   * Partout ailleurs elle se tait — une barre en avant permanente crie « remplis-moi » à
   * chaque écran, et la planche ne lui donne ce ton qu'une fois (EtatFeuilleVide, Ajouter).
   */
  vive?: boolean;
  /**
   * Le champ, tenu par la feuille : sur une feuille vide, « + La première chose à faire »
   * y pose le curseur (retour 5 des testeurs, ticket 04).
   */
  champ?: RefObject<HTMLInputElement | null>;
  onAjouter: (titre: string) => void;
}) {
  const [titre, setTitre] = useState('');
  const champInterne = useRef<HTMLInputElement>(null);
  const champ = champDeLaFeuille ?? champInterne;

  function soumettre(evenement: FormEvent) {
    evenement.preventDefault();
    const propre = titre.trim();
    if (!propre) return;
    onAjouter(propre);
    setTitre('');
    // La barre reste ouverte et garde le curseur : c'est toute la différence entre
    // « vider sa tête » et « remplir un formulaire ».
    champ.current?.focus();
  }

  const teinte = teinteOuAccent(couleur);

  return (
    <form
      className={`barre-ajout${vive ? ' barre-ajout--vive' : ''}`}
      data-tuto="barre-ajout"
      onSubmit={soumettre}
      style={{ ['--teinte' as string]: teinte }}
    >
      {vive ? (
        <span className="barre-point" aria-hidden="true" />
      ) : (
        <svg className="barre-plus" width="16" height="16" viewBox="0 0 18 18" fill="none" aria-hidden="true">
          <path d="M9 3v12M3 9h12" stroke="var(--trait-marque)" strokeWidth="1.5" />
        </svg>
      )}

      {vive && (
        <>
          <span className="barre-dans">Dans «&nbsp;{dans}&nbsp;»</span>
          <span className="barre-separateur" aria-hidden="true" />
        </>
      )}

      <input
        ref={champ}
        className="barre-champ"
        data-tuto="barre-champ"
        value={titre}
        onChange={(e) => setTitre(e.target.value)}
        /* Au calme, la destination tient dans l'invite : une ligne de moins pour la dire. */
        placeholder={vive ? 'Écris la première chose à faire…' : `Ajouter dans « ${dans} »`}
        aria-label={`ajouter dans ${dans}`}
        maxLength={BORNES_BULLE.titreMax}
        autoComplete="off"
        spellCheck={false}
      />

      {/*
        Le dessin ne montre aucun bouton : la barre se valide à Entrée, et c'est tout.
        Celui-ci est invisible à l'œil et bien réel pour le clavier et les lecteurs d'écran —
        sans lui, la barre n'offre aucune commande nommée, et le geste le plus fréquent de
        l'app n'existerait que pour qui voit le mot « Entrée » écrit à côté.
      */}
      <button type="submit" className="barre-invisible">
        Ajouter
      </button>

      <span className="barre-indice" aria-hidden="true">
        <kbd>Entrée</kbd>
        <span>crée la bulle</span>
      </span>
    </form>
  );
}
